import React, { useState, useEffect, useRef, useMemo } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { xml2js } from "../../json/xml2js";
import queryString from "query-string";
import { confirmAlert } from "react-confirm-alert";
import "react-confirm-alert/src/react-confirm-alert.css";
import moment from "moment";
import ClientFormHeader from "../common/ClientFormHeader";
import FormButtonBar from "../common/FormButtonBar";
import Signature from "../common/Signature";
import Comment from "../common/Comment";
import BackIcon from "../common/BackIcon";
import ShareModal from "../common/ShareModal";
import {
  Input,
  Select,
  Checkbox,
  TextArea,
  PhoneInput,
} from "../common/ControlBundle";
import {
  getCareTeams,
  getConfig,
  getSubmission,
  saveForm,
  cloneForm,
  deleteFormSubmission,
  validateForm,
  handleFormChange,
  compareForms,
  getFormsySubmissionsForClient,
  sendShareEmail,
  getClientData,
  handleCopyUrl,
} from "../../scripts/formScripts";
import {
  getFieldName,
  getFieldValue,
  getFieldDate,
  getFieldText,
  ArrayCheck,
} from "../../scripts/helperScripts";
import { getSchema } from "./joi/validation";

function OHSafetyPlan() {
  const location = useLocation();
  const navigate = useNavigate();
  const initialProgram = location.state?.selectedProgram;

  //Get route and query string params
  const { clientid, formid, submissionid } = useParams();
  const qs = queryString.parse(location.search);
  console.log("Params", clientid, formid, submissionid);
  console.log("QS:", qs);

  //Get logged in user info
  const safyInfo = JSON.parse(localStorage.getItem("safyInfo"));
  //The complete Form Submission object with form data
  const [formsyDetails, setFormsyDetails] = useState({});
  const [formsyConfig, setFormsyConfig] = useState({});
  const [loadingConfig, setLoadingConfig] = useState(false);
  //The form data and previous forms
  const [data, setData] = useState({});
  const [allFormSubmissions, setAllFormSubmissions] = useState([]);
  //Touched fields
  const [touched, setTouched] = useState({});
  const [isSaveInProgress, setIsSaveInProgress] = useState(false);
  //Errors
  const [errors, setErrors] = useState({});
  //Client, Program and CareTeam Info
  const [clientData, setClientData] = useState({});
  const [careTeams, setCareTeams] = useState([]);

  //Signatures State Property
  const [signatures, setSignatures] = useState([]);
  //Show Signature Modal
  const [showSignatureModal, setShowSignatureModal] = useState(false);
  //Comments state Property
  const [comments, setComments] = useState();
  //Show Comments Modal
  const [showComment, setShowComment] = useState(false);
  //Show Share Modal
  const [showShare, setShowShare] = useState(false);
  //Show Form Level (not field specific) Comments
  const [showFormComment, setShowFormComment] = useState(false);

  const [doCompare, setDoCompare] = useState(false);
  const [timeoutId, setTimeoutId] = useState();

  const pdfRef = useRef(true);
  const isMountedRef = useRef(true);

  //Get validation schema
  const schema = getSchema(formid);
  console.log("Schema Config: ", schema);

  const selectedProgramId = useMemo(() => {
    return data.SelectedProgram?.["@value"];
  }, [data.SelectedProgram]);

  const formDisabled = useMemo(() => {
    return formsyDetails.Active === "0" || formsyDetails.FinalSigned === "1";
  }, [formsyDetails]);

  useEffect(() => {
    return () => {
      isMountedRef.current = false;
      pdfRef.current = false;
    };
  }, []);

  useEffect(() => {
    if (
      !initialProgram?.ccx_client_programid ||
      !data.SelectedProgram ||
      data.SelectedProgram["@value"]
    ) {
      return;
    }

    const obj = { ...data };
    obj.SelectedProgram["@value"] = initialProgram.ccx_client_programid;
    setData(obj);

    if (formsyDetails.ClientProgramId === initialProgram.ccx_client_programid) {
      return;
    }

    const details = { ...formsyDetails };
    details.ClientProgramId = initialProgram.ccx_client_programid;
    details.ClientProgramName = getFieldName(initialProgram.ccx_programunit);
    setFormsyDetails(details);
  }, [initialProgram, selectedProgramId, data, formsyDetails]);

  //Get Client Info
  useEffect(() => {
    if (!formid || !clientid) {
      return;
    }

    const loadClientInfo = async () => {
      const response = await Promise.all([
        getFormsySubmissionsForClient(formid, clientid),
        getCareTeams(clientid),
      ]);

      if (!isMountedRef.current || !response) {
        return;
      }

      setAllFormSubmissions(response[0]);
      setCareTeams(response[1]);
    };

    loadClientInfo();

    if (submissionid !== undefined) {
      const loadSubmission = async () => {
        const response = await getSubmission(submissionid);

        if (!isMountedRef.current || !response) {
          return;
        }

        setData(response.Data);
        setFormsyDetails(response.FormsyDetails);

        if (response.Comments) {
          setComments(response.Comments);
        }

        if (response.Signatures) {
          setSignatures(response.Signatures);
        }
      };

      loadSubmission();
    }
  }, [formid, clientid, submissionid]);

  useEffect(() => {
    if (
      !formid ||
      !clientid ||
      Object.keys(formsyConfig).length > 0 ||
      loadingConfig ||
      !safyInfo?.user
    ) {
      return;
    }

    const loadConfig = async () => {
      setLoadingConfig(true);
      const response = await getConfig(formid);

      if (!isMountedRef.current || !response) {
        return;
      }

      setFormsyConfig(response);
      const clientDataResponse = await getClientData(
        clientid,
        submissionid !== undefined,
        `${response.FormName}${
          response.VersionIntId > 1 ? `v${response.VersionIntId}` : ""
        }`
      );

      if (!isMountedRef.current || !clientDataResponse) {
        return;
      }

      setClientData(clientDataResponse);

      if (!submissionid) {
        //Set Formsy Details
        const newForm = {
          SubmissionId: "",
          FormId: response.FormId,
          FormVersionId: formid,
          ContactId: clientid,
          ContactName: clientDataResponse.client.fullName,
          ClientProgramId: "",
          ClientProgramName: "",
          TertiaryObjectId: "",
          TertiaryObjectName: "",
          FormResults: {
            results: {},
          },

          Comments: "",
          Signatures: "",
          CreatedOn: "",
          CreatedById: safyInfo.user.azureId,
          CreatedByName: safyInfo.user.displayName,
          ModifiedById: safyInfo.user.azureId,
          ModifiedByName: safyInfo.user.displayName,
          FinalSigned: "0",
          FinalSignedDate: "",
          Active: "1",
        };
        setFormsyDetails(newForm);
        setData(response.FormConfiguration[0].config[0]);
        setLoadingConfig(false);

        console.log("New Form:", newForm);
        console.log(
          "loadConfig setData:",
          response.FormConfiguration[0].config[0]
        );
      }
    };

    loadConfig();
  }, [
    loadingConfig,
    formid,
    clientid,
    safyInfo?.user,
    formsyConfig,
    submissionid,
  ]);

  useEffect(() => {
    if (!submissionid || !!formsyDetails.SubmissionId) {
      return;
    }

    const obj = { ...formsyDetails };
    obj.SubmissionId = submissionid;
    setFormsyDetails(obj);
  }, [submissionid, formsyDetails]);

  const helperConfiguration = useMemo(() => {
    try {
      return JSON.parse(formsyConfig.HelperConfiguration[0]);
    } catch {
      const defaultConfig = {
        buttonBarConfig: {
          sign: {
            display: false,
            initialSignatureType: "full",
          },
          clone: {
            display: false,
          },
          delete: {
            display: false,
          },
          share: {
            display: false,
          },
          comment: {
            display: false,
          },
        },
        signatureType: "full",
        finalSignType: "Provider",
      };

      return defaultConfig;
    }
  }, [formsyConfig.HelperConfiguration]);

  const getSelectedProgramName = (dataObj, cData) => {
    if (!dataObj.SelectedProgram || !cData.programs) {
      return "";
    }

    let name = getFieldName(dataObj.SelectedProgram);
    if (name) {
      return name;
    } else {
      const selectedProgram = cData.programs.filter(
        (p) => p.ccx_client_programid === getFieldValue(dataObj.SelectedProgram)
      )[0];

      if (selectedProgram) {
        name = getFieldName(selectedProgram.ccx_programunit);
        const programDates = ` (${getFieldDate(
          selectedProgram.ccx_admitdate
        )} - ${
          selectedProgram.ccx_dischargedate
            ? getFieldDate(selectedProgram.ccx_dischargedate)
            : "no date"
        })`;
        return name + programDates;
      } else {
        return "";
      }
    }
  };

  //Form validation
  const validate = () => {
    const results = validateForm(data, schema);
    setErrors(results);
    return results;
  };

  const handleChange = ({ currentTarget: input }) => {
    if (timeoutId) {
      clearTimeout(timeoutId);
    }

    const result = handleFormChange(input, data, schema, errors);
    setData(result.Data);
    setErrors(result.Errors);

    if (!submissionid) {
      return result.Data;
    }

    const touchedObj = { ...touched, [input.name]: true };
    const touchedFields = Object.keys(touchedObj).filter(
      (field) => touchedObj[field]
    );

    if (touchedFields.length >= 5) {
      if (isSaveInProgress) {
        const toId = setTimeout(() => {
          touchedFields.forEach((field) => (touchedObj[field] = false));
          handleSubmit(result.Data, null, null, null, true);
        }, 20000);

        setTimeoutId(toId);
      } else {
        clearTimeout(timeoutId);
        handleSubmit(result.Data, null, null, null, true);
        touchedFields.forEach((field) => (touchedObj[field] = false));
      }
    } else {
      const toId = setTimeout(() => {
        if (isSaveInProgress) {
          const toId2 = setTimeout(() => {
            touchedFields.forEach((field) => (touchedObj[field] = false));
            handleSubmit(result.Data, null, null, null, true);
          }, 20000);

          setTimeoutId(toId2);
        } else {
          touchedFields.forEach((field) => (touchedObj[field] = false));
          handleSubmit(result.Data, null, null, null, true);
        }
      }, 20000);

      setTimeoutId(toId);
    }

    setTouched(touchedObj);
    return result.Data;
  };

  ////SAVE
  const handleSubmit = async (
    dataOverride,
    formsyDetailsOverride,
    commentsOverride,
    signaturesOverride,
    autoSave = false
  ) => {
    if (isSaveInProgress) {
      return;
    }

    const dataObj = dataOverride || data;
    const formsyDetailsObj = formsyDetailsOverride || formsyDetails;
    const commentsObj =
      commentsOverride === undefined ? undefined : commentsOverride || comments;
    const signaturesObj = signaturesOverride || signatures;

    if (timeoutId) {
      clearTimeout(timeoutId);
    }

    setTouched({});
    const errs = { ...errors };

    if (!dataObj.SelectedProgram?.["@value"]) {
      errs.SelectedProgram = '"Selected Program" is required';
      setErrors(errs);
      return;
    }

    delete errs.SelectedProgram;
    setErrors(errs);
    setIsSaveInProgress(true);

    try {
      const submissionResponse = await saveForm(
        dataObj,
        formsyDetailsObj,
        commentsObj,
        signaturesObj,
        navigate,
        location,
        autoSave
      );

      if (!isMountedRef.current || !submissionResponse?.Id) {
        return;
      }

      const newDetails = { ...formsyDetailsObj };
      newDetails.ModifiedOn = submissionResponse.LastModifiedOn;
      newDetails.ModifiedById = safyInfo.user.azureId;
      newDetails.ModifiedByName = safyInfo.user.displayName;

      if (newDetails.SubmissionId === "") {
        newDetails.SubmissionId = submissionResponse?.Id;
      }

      setFormsyDetails(newDetails);
    } finally {
      setIsSaveInProgress(false);
    }
  };

  ////CLONE
  const handleOnClone = (event) => {
    event.preventDefault();
    cloneForm(
      data,
      formsyDetails,
      comments,
      signatures,
      navigate,
      location,
      formsyConfig
    );
    setSignatures([]);
  };

  ////DELETE
  const handleDelete = (e) => {
    e.preventDefault();
    if (submissionid) {
      deleteFormSubmission(
        data,
        formsyDetails,
        comments,
        signatures,
        navigate,
        location
      );
    } else {
      alert("The form could not be deleted because it has not been saved");
    }
  };

  ////SHARE
  const toggleShareModal = () => {
    setShowShare(!showShare);
  };

  const handleShareSend = (recipientList, shareComments) => {
    sendShareEmail(
      recipientList,
      shareComments,
      location.pathname,
      data,
      formsyDetails,
      comments,
      signatures,
      formsyConfig.FormDescription
    );
  };

  ////COMMENTS FUNCTIONALITY
  const [commentToEdit, setCommentToEdit] = useState();

  const toggleCommentModal = (editComment) => {
    if (!showComment && editComment) setCommentToEdit(editComment);
    setShowComment(!showComment);
  };

  const handleComments = ({ action, fieldName, comment, acknowledge }) => {
    if (action === "ADD") {
      setShowComment(true);
    } else if (comment !== undefined) {
      if (action === "EDIT") {
        toggleCommentModal(comment);
      } else if (action === "DELETE") {
        handleDeleteComment(comment);
      } else if (action === "ACKNOWLEDGE" && acknowledge !== undefined) {
        handleAcknowledgeComment(comment, acknowledge);
      }
    } else if (action === "GET") {
      return parsedComments.filter(
        (comment) => comment.field._text === fieldName
      );
    }
  };

  const commentFields = () => {
    let commentOptions = [];
    commentOptions.push({ _id: "EntireForm", name: "Entire Form----" });
    for (const property in data) {
      if (
        data[property]["@type"] !== "section" &&
        data[property]["@type"] !== "label" &&
        data[property]["@type"] !== "User" &&
        data[property]["@type"] !== "hidden"
      ) {
        const option = {
          _id: property,
          name:
            data[property]["@label"] !== undefined &&
            data[property]["@label"] !== ""
              ? data[property]["@label"]
              : property,
        };
        commentOptions.push(option);
      }
    }
    return commentOptions;
  };

  const parsedComments = useMemo(() => {
    if (!comments) {
      return [];
    }

    const commentObj = xml2js(`<comments type="array">${comments}</comments>`, {
      compact: true,
      spaces: 4,
    });

    return ArrayCheck(commentObj.comments.comment);
  }, [comments]);

  const onCommentSave = (comment, commentToEdit) => {
    let formsyComments;
    if (commentToEdit) {
      const commentXml = `<comment><createdby>${
        commentToEdit.createdby._text
      }</createdby><datetime>${commentToEdit.datetime._text}</datetime><field>${
        commentToEdit.field._text
      }</field><comment>${commentToEdit.comment._text}</comment>${
        commentToEdit.acknowledgedby
          ? `<acknowledgedby>${commentToEdit.acknowledgedby._text}</acknowledgedby><acknowledgedon>${commentToEdit.acknowledgedon._text}</acknowledgedon>`
          : ""
      }</comment>`;
      console.log("CurrentCommentsXML: ", comments);
      console.log("CommentToReplace: ", commentXml);
      console.log("CommentToEdit: ", commentToEdit);
      console.log("CommentToEditXML: ", comment);

      formsyComments =
        comments !== undefined
          ? comments.replace(commentXml, comment)
          : comment;
      setComments(formsyComments);
    } else {
      formsyComments = comments !== undefined ? comments + comment : comment;
      setComments(formsyComments);
    }

    setShowComment(false);
    handleSubmit(null, null, formsyComments, null);
    setCommentToEdit(undefined);
  };

  const handleDeleteComment = (comment) => {
    const commentXml = `<comment><createdby>${
      safyInfo.user.displayName
    }</createdby><datetime>${comment.datetime._text}</datetime><field>${
      comment.field._text
    }</field><comment>${comment.comment._text}</comment>${
      comment.acknowledgedby
        ? `<acknowledgedby>${comment.acknowledgedby._text}</acknowledgedby><acknowledgedon>${comment.acknowledgedon._text}</acknowledgedon>`
        : ""
    }</comment>`;

    let formsyComments = comments.replace(commentXml, "");
    if (formsyComments === "") formsyComments = undefined;
    setComments(formsyComments);
    handleSubmit(null, null, formsyComments, null);
  };

  const toggleCommentToEdit = (comment) => {
    setCommentToEdit(comment);
    setShowComment(true);
  };

  const handleAcknowledgeComment = (comment, acknowledge) => {
    if (acknowledge) {
      const oldCommentXML = `<comment><createdby>${comment.createdby._text}</createdby><datetime>${comment.datetime._text}</datetime><field>${comment.field._text}</field><comment>${comment.comment._text}</comment></comment>`;
      const newCommentXML = `<comment><createdby>${
        comment.createdby._text
      }</createdby><datetime>${comment.datetime._text}</datetime><field>${
        comment.field._text
      }</field><comment>${comment.comment._text}</comment><acknowledgedby>${
        safyInfo.user.displayName
      }</acknowledgedby><acknowledgedon>${moment().format(
        "L"
      )} ${moment().format("LT")}</acknowledgedon></comment>`;
      const formsyComments = comments.replace(oldCommentXML, newCommentXML);
      setComments(formsyComments);
      handleSubmit(null, null, formsyComments, null);
    } else {
      const oldCommentXML = `<comment><createdby>${comment.createdby._text}</createdby><datetime>${comment.datetime._text}</datetime><field>${comment.field._text}</field><comment>${comment.comment._text}</comment><acknowledgedby>${comment.acknowledgedby._text}</acknowledgedby><acknowledgedon>${comment.acknowledgedon._text}</acknowledgedon></comment>`;
      const newCommentXML = `<comment><createdby>${comment.createdby._text}</createdby><datetime>${comment.datetime._text}</datetime><field>${comment.field._text}</field><comment>${comment.comment._text}</comment></comment>`;
      const formsyComments = comments.replace(oldCommentXML, newCommentXML);
      setComments(formsyComments);
      handleSubmit(null, null, formsyComments, null);
    }
  };

  const handleCommentNavigation = (fieldName) => {
    const element = document.getElementById(fieldName);
    if (element.type === "checkbox")
      scrollTo(
        document.getElementById(fieldName).parentElement.parentElement
          .parentElement.offsetTop - 50,
        () => {
          document.getElementById(fieldName).focus();
        }
      );
    else
      scrollTo(document.getElementById(fieldName).offsetTop - 50, () => {
        document.getElementById(fieldName).focus();
      });
  };

  const renderComments = () => {
    return parsedComments
      .filter(
        (comment) =>
          comment.field._text === "EntireForm" ||
          comment.field._text === "Entire Form----"
      )
      .map((comment, index) => (
        <div className="mt-2" key={`form-comment-${index}`}>
          {showFormComment && (
            <div
              className={`${
                comment.acknowledgedby ? "bg-safy3-50" : "bg-warning"
              } p-2 m-2 rounded d-print-none`}
              title={
                comment.acknowledgedby
                  ? `Acknowledged by: ${comment.acknowledgedby._text} ${comment.acknowledgedon._text}`
                  : ""
              }
            >
              <span className="h6">
                {comment.createdby._text ===
                  JSON.parse(localStorage.getItem("safyInfo")).user
                    .displayName && (
                  <>
                    <i
                      title="Delete Comment"
                      className="far fa-trash-alt text-white me-2"
                      onClick={() => {
                        confirmAlert({
                          title: "Delete Comment",
                          message:
                            "Are you sure you want to delete this comment?",
                          buttons: [
                            {
                              label: "Yes",
                              onClick: () => {
                                handleDeleteComment(comment);
                              },
                            },
                            {
                              label: "No",
                              onClick: () => {
                                /* do nothing */
                              },
                            },
                          ],
                        });
                      }}
                    ></i>
                    {!comment.acknowledgedby && (
                      <i
                        title="Edit Comment"
                        className="fas fa-pen text-white me-2"
                        onClick={(e) => {
                          toggleCommentToEdit(comment);
                        }}
                      ></i>
                    )}
                  </>
                )}
                {comment.acknowledgedby && (
                  <i
                    className="far fa-check-square text-white me-2"
                    onClick={() => {
                      if (
                        comment.acknowledgedby._text ===
                        JSON.parse(localStorage.getItem("safyInfo")).user
                          .displayName
                      )
                        handleAcknowledgeComment(comment, false);
                    }}
                  ></i>
                )}
                {!comment.acknowledgedby && (
                  <i
                    className="far fa-square text-white me-2"
                    onClick={() => {
                      handleAcknowledgeComment(comment, true);
                    }}
                    title="Acknowledge Comment"
                  ></i>
                )}
                <br />
                {`${comment.createdby._text} - ${comment.datetime._text}`}
              </span>
              <br />
              Regarding:{" "}
              {comment.field._text !== "EntireForm" &&
              comment.field._text !== "Entire Form----" ? (
                <button
                  className="btn btn-link"
                  onClick={() => {
                    setTimeout(() => {
                      handleCommentNavigation(comment.field._text);
                    }, 100);
                  }}
                >
                  {comment.field._text}
                </button>
              ) : (
                <>{comment.field._text}</>
              )}
              <br />
              {comment.comment._text}
            </div>
          )}
        </div>
      ));
  };

  function scrollTo(offset, callback) {
    const fixedOffset = offset.toFixed();
    const onScroll = function () {
      if (window.pageYOffset.toFixed() === fixedOffset) {
        window.removeEventListener("scroll", onScroll);
        callback();
      }
    };

    window.addEventListener("scroll", onScroll);
    onScroll();
    window.scrollTo({
      top: offset,
      behavior: "smooth",
    });
  }

  //SIGNATURE FUNCTIONALITY
  const toggleSignatureModal = (e) => {
    e.preventDefault();
    if (!isSaveInProgress) setShowSignatureModal(true);
  };

  const saveSignature = async (signatureData) => {
    console.log("Signature Data:", signatureData);

    let sigArray = [];
    if (Array.isArray(signatureData)) {
      sigArray = signatureData;
    } else {
      sigArray.push(signatureData);
    }
    setSignatures(sigArray);

    const formsyDetailsObj = { ...formsyDetails };

    if (formsyDetailsObj.FinalSigned !== "1" && checkFinalSigned(sigArray)) {
      console.log("Final Signed");
      formsyDetailsObj.FinalSigned = "1";

      if (
        formsyDetailsObj.FinalSignedDate === "" ||
        formsyDetailsObj.FinalSignedDate === "1900-01-01T00:00:00"
      ) {
        formsyDetailsObj.FinalSignedDate = new Date();
      }

      setFormsyDetails(formsyDetailsObj);
      setComments(undefined);
    }

    handleSubmit(
      null,
      formsyDetailsObj,
      formsyDetailsObj.FinalSigned === "1" ? undefined : null,
      sigArray
    );
  };

  const checkFinalSigned = (sigArray) => {
    const requiredSignatureTypes = helperConfiguration.finalSignType.split(",");

    const capturedSignatureTypes = sigArray.map((sig) => sig.signatureType);
    console.log("requiredSignatureTypes: ", requiredSignatureTypes);
    console.log("capturedSignatureTypes: ", capturedSignatureTypes);

    const missingSigTypes = requiredSignatureTypes.filter(
      (type) => !capturedSignatureTypes.includes(type)
    );

    return missingSigTypes.length === 0;
  };

  //COMPARE
  const compare = (field) => {
    if (doCompare)
      return compareForms(field, data, allFormSubmissions, formsyDetails);
  };

  const toggleCompare = () => {
    setDoCompare(!doCompare);
  };

  //CUSTOM
  const selectedProgramType = useMemo(() => {
    if (!data.SelectedProgram?.["@value"] || !clientData.programs) {
      return "";
    }

    const selectedProgram = clientData.programs.filter(
      (prog) => prog.ccx_client_programid === data.SelectedProgram["@value"]
    )[0];
    return getFieldText(selectedProgram["unit.ccx_programtype"]);
  }, [data.SelectedProgram, clientData.programs]);

  ////UI
  return (
    <>
      <div className="mb-2">
        <BackIcon />
      </div>
      <div className={!submissionid ? "watermarked" : ""} ref={pdfRef}>
        <ClientFormHeader
          formTitle={formsyConfig.FormDescription}
          clientData={clientData.client}
          formsyDetails={formsyDetails}
        ></ClientFormHeader>
        <FormButtonBar
          config={
            formsyConfig.HelperConfiguration
              ? JSON.parse(formsyConfig.HelperConfiguration[0]).buttonBarConfig
              : {
                  sign: {
                    display: false,
                    initialSignatureType: "supervisor",
                  },
                  clone: {
                    display: false,
                  },
                  delete: {
                    display: false,
                  },
                  share: {
                    display: false,
                  },
                  comment: {
                    display: false,
                  },
                }
          }
          onSave={handleSubmit}
          onClone={handleOnClone}
          onDelete={handleDelete}
          onSignClicked={toggleSignatureModal}
          onComment={toggleCommentModal}
          onCompare={toggleCompare}
          onShare={toggleShareModal}
          signed={signatures.length > 0}
          finalSigned={formsyDetails.FinalSigned === "1"}
          validate={validate}
          pdfRef={pdfRef}
          filename={`${formsyConfig.FormDescription}`}
          newestVersion={
            formsyConfig.VersionIntId === formsyConfig.LatestVersionIntId
          }
        />
        <div className="py-2 border-bottom border-dark">
          <b>Selected Client Program:</b>{" "}
          {getSelectedProgramName(data, clientData)}
        </div>
        {renderComments()}
        {parsedComments.filter(
          (comment) =>
            comment.field._text === "EntireForm" ||
            comment.field._text === "Entire Form----"
        ).length > 0 && (
          <i
            className="fas fa-comment-alt text-warning float-end mb-2 ms-2"
            onClick={() => setShowFormComment(!showFormComment)}
          ></i>
        )}
        <div className="mt-4">
          {data && Object.keys(data).length > 0 && (
            <>
              <div className="clearfix d-print-none">
                <div className="float-end">
                  <button
                    className="btn btn-link"
                    title="Copy and Open Encounter"
                    onClick={() => {
                      const encounterEnvironment =
                        process.env.REACT_APP_ENVIRONMENT === "Production"
                          ? "ccplive-cp"
                          : "d365base-cp";
                      handleCopyUrl(location.pathname).then(() => {
                        window.open(
                          `https://${encounterEnvironment}.safy.org/dist/EncounterPortal/index.html?contactid=%7B${clientid}%7D`
                        );
                      });
                    }}
                  >
                    Copy form link and launch to Encounter
                  </button>
                </div>
              </div>
              <p>
                <i>
                  When the careteam determines a client is at risk of harm to
                  self or others a safety plan is created using this document.
                  It can be changed only when the team agrees to the change and
                  should be reviewed at every session held. Formalized review of
                  the plan should coincide with Care Plan Reviews and be
                  documented in the careplan.
                </i>
              </p>
              {selectedProgramType === "186460001" && (
                <p>
                  <i>
                    ***If you have been hospitalized for any mental health
                    event, please call us at 419-222-1527. We will want to
                    schedule follow up care as quickly as possible after your
                    discharge.
                  </i>
                </p>
              )}
              <Input
                name="SafetyPlanDate"
                label="Safety Plan Date:"
                type="date"
                error={errors.SafetyPlanDate}
                value={data.SafetyPlanDate["@value"]}
                onChange={handleChange}
                commentsHandler={handleComments}
                disabled={formDisabled}
              />
              <TextArea
                name="CareTeam"
                label="Care Team:"
                error={errors.CareTeam}
                value={data.CareTeam["@value"]}
                onChange={handleChange}
                identical={compare("CareTeam")}
                commentsHandler={handleComments}
                disabled={formDisabled}
              />
              <div className="row">
                <div className="col">
                  <TextArea
                    name="Guardian"
                    label="Parents/Guardian:"
                    error={errors.Guardian}
                    value={data.Guardian["@value"]}
                    onChange={handleChange}
                    identical={compare("Guardian")}
                    commentsHandler={handleComments}
                    disabled={formDisabled}
                  />
                </div>
                <div className="col">
                  <TextArea
                    name="GuardianContact"
                    label="Contact Info:"
                    error={errors.GuardianContact}
                    value={data.GuardianContact["@value"]}
                    onChange={handleChange}
                    identical={compare("GuardianContact")}
                    commentsHandler={handleComments}
                    disabled={formDisabled}
                  />
                </div>
              </div>
              <div className="row">
                <div className="col">
                  <TextArea
                    name="FosterParent"
                    label="Foster Mother/Father:"
                    error={errors.FosterParent}
                    value={data.FosterParent["@value"]}
                    onChange={handleChange}
                    identical={compare("FosterParent")}
                    commentsHandler={handleComments}
                    disabled={formDisabled}
                  />
                </div>
                <div className="col">
                  <TextArea
                    name="FosterParentContact"
                    label="Contact Info:"
                    error={errors.FosterParentContact}
                    value={data.FosterParentContact["@value"]}
                    onChange={handleChange}
                    identical={compare("FosterParentContact")}
                    commentsHandler={handleComments}
                    disabled={formDisabled}
                  />
                </div>
              </div>
              <div className="row">
                <div className="col">
                  <TextArea
                    name="Relatives"
                    label="Relatives:"
                    error={errors.Relatives}
                    value={data.Relatives["@value"]}
                    onChange={handleChange}
                    identical={compare("Relatives")}
                    commentsHandler={handleComments}
                    disabled={formDisabled}
                  />
                </div>
                <div className="col">
                  <TextArea
                    name="RelativesContact"
                    label="Contact Info:"
                    error={errors.RelativesContact}
                    value={data.RelativesContact["@value"]}
                    onChange={handleChange}
                    identical={compare("RelativesContact")}
                    commentsHandler={handleComments}
                    disabled={formDisabled}
                  />
                </div>
              </div>
              <div className="row">
                <div className="col">
                  <TextArea
                    name="Teachers"
                    label="Teachers:"
                    error={errors.Teachers}
                    value={data.Teachers["@value"]}
                    onChange={handleChange}
                    identical={compare("Teachers")}
                    commentsHandler={handleComments}
                    disabled={formDisabled}
                  />
                </div>
                <div className="col">
                  <TextArea
                    name="TeachersContact"
                    label="Contact Info:"
                    error={errors.TeachersContact}
                    value={data.TeachersContact["@value"]}
                    onChange={handleChange}
                    identical={compare("TeachersContact")}
                    commentsHandler={handleComments}
                    disabled={formDisabled}
                  />
                </div>
              </div>
              <div className="row">
                <div className="col">
                  <TextArea
                    name="ProbationOfficer"
                    label="Probation Officer:"
                    error={errors.ProbationOfficer}
                    value={data.ProbationOfficer["@value"]}
                    onChange={handleChange}
                    identical={compare("ProbationOfficer")}
                    commentsHandler={handleComments}
                    disabled={formDisabled}
                  />
                </div>
                <div className="col">
                  <TextArea
                    name="ProbationOfficerContact"
                    label="Contact Info:"
                    error={errors.ProbationOfficerContact}
                    value={data.ProbationOfficerContact["@value"]}
                    onChange={handleChange}
                    identical={compare("ProbationOfficerContact")}
                    commentsHandler={handleComments}
                    disabled={formDisabled}
                  />
                </div>
              </div>
              <div className="row">
                <div className="col">
                  <TextArea
                    name="ServicesWorker"
                    label="Children’s Services Worker:"
                    error={errors.ServicesWorker}
                    value={data.ServicesWorker["@value"]}
                    onChange={handleChange}
                    identical={compare("ServicesWorker")}
                    commentsHandler={handleComments}
                    disabled={formDisabled}
                  />
                </div>
                <div className="col">
                  <TextArea
                    name="ServicesWorkerContact"
                    label="Contact Info:"
                    error={errors.ServicesWorkerContact}
                    value={data.ServicesWorkerContact["@value"]}
                    onChange={handleChange}
                    identical={compare("ServicesWorkerContact")}
                    commentsHandler={handleComments}
                    disabled={formDisabled}
                  />
                </div>
              </div>
              <div className="row">
                <div className="col">
                  <TextArea
                    name="Counselor"
                    label="Counselor:"
                    error={errors.Counselor}
                    value={data.Counselor["@value"]}
                    onChange={handleChange}
                    identical={compare("Counselor")}
                    commentsHandler={handleComments}
                    disabled={formDisabled}
                  />
                </div>
                <div className="col">
                  <TextArea
                    name="CounselorContact"
                    label="Contact Info:"
                    error={errors.CounselorContact}
                    value={data.CounselorContact["@value"]}
                    onChange={handleChange}
                    identical={compare("CounselorContact")}
                    commentsHandler={handleComments}
                    disabled={formDisabled}
                  />
                </div>
              </div>
              <div className="row">
                <div className="col">
                  <TextArea
                    name="OthersResponsible"
                    label="Other adults responsible for safety and supervision:"
                    error={errors.OthersResponsible}
                    value={data.OthersResponsible["@value"]}
                    onChange={handleChange}
                    identical={compare("OthersResponsible")}
                    commentsHandler={handleComments}
                    disabled={formDisabled}
                  />
                </div>
                <div className="col">
                  <TextArea
                    name="OthersResponsibleContact"
                    label="Contact Info:"
                    error={errors.OthersResponsibleContact}
                    value={data.OthersResponsibleContact["@value"]}
                    onChange={handleChange}
                    identical={compare("OthersResponsibleContact")}
                    commentsHandler={handleComments}
                    disabled={formDisabled}
                  />
                </div>
              </div>
              <br />
              <h5>
                <b>
                  <u>SAFETY PLAN:</u>
                </b>
              </h5>
              <TextArea
                name="SafetyPlan"
                rows="5"
                error={errors.SafetyPlan}
                value={data.SafetyPlan["@value"]}
                onChange={handleChange}
                identical={compare("SafetyPlan")}
                commentsHandler={handleComments}
                disabled={formDisabled}
              />
              <br />
              <h5>
                <b>
                  <u>Warning Signs of unsafe behaviors:</u>
                </b>
              </h5>
              <Input
                name="WarningSign1"
                error={errors.WarningSign1}
                value={data.WarningSign1["@value"]}
                onChange={handleChange}
                commentsHandler={handleComments}
                disabled={formDisabled}
              />
              <Input
                name="WarningSign2"
                error={errors.WarningSign2}
                value={data.WarningSign2["@value"]}
                onChange={handleChange}
                commentsHandler={handleComments}
                disabled={formDisabled}
              />
              <Input
                name="WarningSign3"
                error={errors.WarningSign3}
                value={data.WarningSign3["@value"]}
                onChange={handleChange}
                commentsHandler={handleComments}
                disabled={formDisabled}
              />
              <Input
                name="WarningSign4"
                error={errors.WarningSign4}
                value={data.WarningSign4["@value"]}
                onChange={handleChange}
                commentsHandler={handleComments}
                disabled={formDisabled}
              />
              <Input
                name="WarningSign5"
                error={errors.WarningSign5}
                value={data.WarningSign5["@value"]}
                onChange={handleChange}
                commentsHandler={handleComments}
                disabled={formDisabled}
              />
              {data.WarningSign5["@value"] !== undefined && (
                <>
                  <Input
                    name="WarningSign6"
                    error={errors.WarningSign6}
                    value={data.WarningSign6["@value"]}
                    onChange={handleChange}
                    commentsHandler={handleComments}
                    disabled={formDisabled}
                  />
                </>
              )}
              {data.WarningSign6["@value"] !== undefined && (
                <>
                  <Input
                    name="WarningSign7"
                    error={errors.WarningSign7}
                    value={data.WarningSign7["@value"]}
                    onChange={handleChange}
                    commentsHandler={handleComments}
                    disabled={formDisabled}
                  />
                </>
              )}
              {data.WarningSign7["@value"] !== undefined && (
                <>
                  <Input
                    name="WarningSign8"
                    error={errors.WarningSign8}
                    value={data.WarningSign8["@value"]}
                    onChange={handleChange}
                    commentsHandler={handleComments}
                    disabled={formDisabled}
                  />
                </>
              )}
              {data.WarningSign8["@value"] !== undefined && (
                <>
                  <Input
                    name="WarningSign9"
                    error={errors.WarningSign9}
                    value={data.WarningSign9["@value"]}
                    onChange={handleChange}
                    commentsHandler={handleComments}
                    disabled={formDisabled}
                  />
                </>
              )}
              {data.WarningSign9["@value"] !== undefined && (
                <>
                  <Input
                    name="WarningSign10"
                    error={errors.WarningSign10}
                    value={data.WarningSign10["@value"]}
                    onChange={handleChange}
                    commentsHandler={handleComments}
                    disabled={formDisabled}
                  />
                </>
              )}
              <br />
              <h5>
                <b>
                  <u>Identify ways to keep self safe &amp; calm:</u>
                </b>
              </h5>
              <Input
                name="KeepSafe1"
                error={errors.KeepSafe1}
                value={data.KeepSafe1["@value"]}
                onChange={handleChange}
                commentsHandler={handleComments}
                disabled={formDisabled}
              />
              <Input
                name="KeepSafe2"
                error={errors.KeepSafe2}
                value={data.KeepSafe2["@value"]}
                onChange={handleChange}
                commentsHandler={handleComments}
                disabled={formDisabled}
              />
              <Input
                name="KeepSafe3"
                error={errors.KeepSafe3}
                value={data.KeepSafe3["@value"]}
                onChange={handleChange}
                commentsHandler={handleComments}
                disabled={formDisabled}
              />
              <Input
                name="KeepSafe4"
                error={errors.KeepSafe4}
                value={data.KeepSafe4["@value"]}
                onChange={handleChange}
                commentsHandler={handleComments}
                disabled={formDisabled}
              />
              <Input
                name="KeepSafe5"
                error={errors.KeepSafe5}
                value={data.KeepSafe5["@value"]}
                onChange={handleChange}
                commentsHandler={handleComments}
                disabled={formDisabled}
              />
              {data.KeepSafe5["@value"] !== undefined && (
                <>
                  <Input
                    name="KeepSafe6"
                    error={errors.KeepSafe6}
                    value={data.KeepSafe6["@value"]}
                    onChange={handleChange}
                    commentsHandler={handleComments}
                    disabled={formDisabled}
                  />
                </>
              )}
              {data.KeepSafe6["@value"] !== undefined && (
                <>
                  <Input
                    name="KeepSafe7"
                    error={errors.KeepSafe7}
                    value={data.KeepSafe7["@value"]}
                    onChange={handleChange}
                    commentsHandler={handleComments}
                    disabled={formDisabled}
                  />
                </>
              )}
              {data.KeepSafe7["@value"] !== undefined && (
                <>
                  <Input
                    name="KeepSafe8"
                    error={errors.KeepSafe8}
                    value={data.KeepSafe8["@value"]}
                    onChange={handleChange}
                    commentsHandler={handleComments}
                    disabled={formDisabled}
                  />
                </>
              )}
              {data.KeepSafe8["@value"] !== undefined && (
                <>
                  <Input
                    name="KeepSafe9"
                    error={errors.KeepSafe9}
                    value={data.KeepSafe9["@value"]}
                    onChange={handleChange}
                    commentsHandler={handleComments}
                    disabled={formDisabled}
                  />
                </>
              )}
              {data.KeepSafe9["@value"] !== undefined && (
                <>
                  <Input
                    name="KeepSafe10"
                    error={errors.KeepSafe10}
                    value={data.KeepSafe10["@value"]}
                    onChange={handleChange}
                    commentsHandler={handleComments}
                    disabled={formDisabled}
                  />
                </>
              )}
              {selectedProgramType === "186460001" && (
                <>
                  <PhoneInput
                    name="CounselorPhone"
                    label="Call SAFY Counselor (8:30am to 5 pm) at this number:"
                    error={errors.CounselorPhone}
                    value={data.CounselorPhone["@value"]}
                    onChange={handleChange}
                    commentsHandler={handleComments}
                    disabled={formDisabled}
                  />
                  <Input
                    value="5pm –8:30am: 800-532-7239 to have therapist paged"
                    disabled
                  />
                  <Input
                    value="Call the Hopeline at 800-567-4673 or text 741741"
                    disabled
                  />
                  <Input
                    value="Call 911 or visit your local Emergency Department"
                    disabled
                  />
                </>
              )}
              {selectedProgramType !== "186460001" && (
                <>
                  <PhoneInput
                    name="CounselorPhone"
                    label="Call SAFY Clinician (8am to 5pm) at this number:"
                    error={errors.CounselorPhone}
                    value={data.CounselorPhone["@value"]}
                    onChange={handleChange}
                    commentsHandler={handleComments}
                    disabled={formDisabled}
                  />
                  <Input
                    value="5pm to 8am: 800-532-7239 to have clinician paged"
                    disabled
                  />
                </>
              )}
              <br />
              <h5>
                <b>
                  <u>DAILY ROUTINE:</u>
                </b>
              </h5>
              <Checkbox
                name="Visible"
                label="Visible at all times"
                checked={data.Visible["@value"] === "true"}
                onChange={handleChange}
                commentsHandler={handleComments}
                disabled={formDisabled}
              />
              <Checkbox
                name="SameArea"
                label="Within same area – not always visible"
                checked={data.SameArea["@value"] === "true"}
                onChange={handleChange}
                commentsHandler={handleComments}
                disabled={formDisabled}
              />
              <Checkbox
                name="ShutDoor"
                label="Never behind shut door"
                checked={data.ShutDoor["@value"] === "true"}
                onChange={handleChange}
                commentsHandler={handleComments}
                disabled={formDisabled}
              />
              <Checkbox
                name="RestrictedAreas"
                label="Restricted from certain areas"
                checked={data.RestrictedAreas["@value"] === "true"}
                onChange={handleChange}
                commentsHandler={handleComments}
                disabled={formDisabled}
              />
              {data.RestrictedAreas["@value"] === "true" && (
                <Input
                  name="RestrictedAreasDetails"
                  label="Please specify (e.g. basement, garage, etc.)"
                  error={errors.RestrictedAreasDetails}
                  value={data.RestrictedAreasDetails["@value"]}
                  onChange={handleChange}
                  commentsHandler={handleComments}
                  disabled={formDisabled}
                />
              )}
              <Checkbox
                name="RestrictedObjects"
                label="Restricted from Sharp Objects"
                checked={data.RestrictedObjects["@value"] === "true"}
                onChange={handleChange}
                commentsHandler={handleComments}
                disabled={formDisabled}
              />
              {data.RestrictedObjects["@value"] === "true" && (
                <Input
                  name="RestrictedObjectsDetails"
                  label="Please specify (e.g. knives, scissors, etc)"
                  error={errors.RestrictedObjectsDetails}
                  value={data.RestrictedObjectsDetails["@value"]}
                  onChange={handleChange}
                  commentsHandler={handleComments}
                  disabled={formDisabled}
                />
              )}
              <Checkbox
                name="OtherRoutine"
                label="Other"
                checked={data.OtherRoutine["@value"] === "true"}
                onChange={handleChange}
                commentsHandler={handleComments}
                disabled={formDisabled}
              />
              {data.OtherRoutine["@value"] === "true" && (
                <>
                  <Input
                    name="OtherRoutineDetails"
                    error={errors.OtherRoutineDetails}
                    value={data.OtherRoutineDetails["@value"]}
                    onChange={handleChange}
                    commentsHandler={handleComments}
                    disabled={formDisabled}
                  />
                </>
              )}
              <br />
              <h5>
                <b>
                  <u>NIGHT/SLEEP TIME LOCATION:</u>
                </b>
              </h5>
              <Checkbox
                name="OwnRoom"
                label="Own Room"
                checked={data.OwnRoom["@value"] === "true"}
                onChange={handleChange}
                commentsHandler={handleComments}
                disabled={formDisabled}
              />
              <Checkbox
                name="RoomWithYouth"
                label="Room with other youth"
                checked={data.RoomWithYouth["@value"] === "true"}
                onChange={handleChange}
                commentsHandler={handleComments}
                disabled={formDisabled}
              />
              <Checkbox
                name="ParentsRoom"
                label="Parent's Room"
                checked={data.ParentsRoom["@value"] === "true"}
                onChange={handleChange}
                commentsHandler={handleComments}
                disabled={formDisabled}
              />
              <Checkbox
                name="CommonArea"
                label="Common area"
                checked={data.CommonArea["@value"] === "true"}
                onChange={handleChange}
                commentsHandler={handleComments}
                disabled={formDisabled}
              />
              <Checkbox
                name="OtherLocation"
                label="Other"
                checked={data.OtherLocation["@value"] === "true"}
                onChange={handleChange}
                commentsHandler={handleComments}
                disabled={formDisabled}
              />
              {data.OtherLocation["@value"] === "true" && (
                <>
                  <Input
                    name="OtherLocationDetails"
                    error={errors.OtherLocationDetails}
                    value={data.OtherLocationDetails["@value"]}
                    onChange={handleChange}
                    commentsHandler={handleComments}
                    disabled={formDisabled}
                  />
                </>
              )}
              <br />
              <h5>
                <b>
                  <u>NIGHT/SLEEP TIME OBSERVATION:</u>
                </b>
              </h5>
              <Checkbox
                name="AdultAsleep"
                label="Adult asleep after youth asleep"
                checked={data.AdultAsleep["@value"] === "true"}
                onChange={handleChange}
                commentsHandler={handleComments}
                disabled={formDisabled}
              />
              <Checkbox
                name="DoorAlarm"
                label="Door alarm to sleep area"
                checked={data.DoorAlarm["@value"] === "true"}
                onChange={handleChange}
                commentsHandler={handleComments}
                disabled={formDisabled}
              />
              <Checkbox
                name="MotionDetector"
                label="Motion detector"
                checked={data.MotionDetector["@value"] === "true"}
                onChange={handleChange}
                commentsHandler={handleComments}
                disabled={formDisabled}
              />
              <Checkbox
                name="OtherObservation"
                label="Other"
                checked={data.OtherObservation["@value"] === "true"}
                onChange={handleChange}
                commentsHandler={handleComments}
                disabled={formDisabled}
              />
              {data.OtherObservation["@value"] === "true" && (
                <>
                  <Input
                    name="OtherObservationDetails"
                    error={errors.OtherObservationDetails}
                    value={data.OtherObservationDetails["@value"]}
                    onChange={handleChange}
                    commentsHandler={handleComments}
                    disabled={formDisabled}
                  />
                </>
              )}
              <br />
              <h5>
                <b>
                  <u>SCHOOL:</u>
                </b>
              </h5>
              <div className="row">
                <div className="col">
                  <Checkbox
                    name="NoSchool"
                    label="Not able to attend school"
                    checked={data.NoSchool["@value"] === "true"}
                    onChange={handleChange}
                    commentsHandler={handleComments}
                    disabled={formDisabled}
                  />
                </div>
                <div className="col">
                  <Checkbox
                    name="EscortToSchool"
                    label="Adult escort to and from school"
                    checked={data.EscortToSchool["@value"] === "true"}
                    onChange={handleChange}
                    commentsHandler={handleComments}
                    disabled={formDisabled}
                  />
                </div>
              </div>
              <div className="row">
                <div className="col">
                  <Checkbox
                    name="EscortInSchool"
                    label="Adult escort throughout school day"
                    checked={data.EscortInSchool["@value"] === "true"}
                    onChange={handleChange}
                    commentsHandler={handleComments}
                    disabled={formDisabled}
                  />
                </div>
                <div className="col">
                  <Checkbox
                    name="EscortToClassrooms"
                    label="Escort between classroom changes"
                    checked={data.EscortToClassrooms["@value"] === "true"}
                    onChange={handleChange}
                    commentsHandler={handleComments}
                    disabled={formDisabled}
                  />
                </div>
              </div>
              <div className="row">
                <div className="col-6">
                  <Checkbox
                    name="SupervisedBathroom"
                    label="Individual or supervised bathroom/locker room"
                    checked={data.SupervisedBathroom["@value"] === "true"}
                    onChange={handleChange}
                    commentsHandler={handleComments}
                    disabled={formDisabled}
                  />
                </div>
              </div>
              <div className="row">
                <div className="col">
                  <Checkbox
                    name="NoEscort"
                    label="No escort during school activities"
                    checked={data.NoEscort["@value"] === "true"}
                    onChange={handleChange}
                    commentsHandler={handleComments}
                    disabled={formDisabled}
                  />
                </div>
                <div className="col">
                  <Checkbox
                    name="SchoolTransportation"
                    label="Uses school transportation"
                    checked={data.SchoolTransportation["@value"] === "true"}
                    onChange={handleChange}
                    commentsHandler={handleComments}
                    disabled={formDisabled}
                  />
                </div>
              </div>
              <div className="row">
                <div className="col-6">
                  <Checkbox
                    name="OtherSchool"
                    label="Other"
                    checked={data.OtherSchool["@value"] === "true"}
                    onChange={handleChange}
                    commentsHandler={handleComments}
                    disabled={formDisabled}
                  />

                  {data.OtherSchool["@value"] === "true" && (
                    <>
                      <Input
                        name="OtherSchoolDetails"
                        error={errors.OtherSchoolDetails}
                        value={data.OtherSchoolDetails["@value"]}
                        onChange={handleChange}
                        commentsHandler={handleComments}
                        disabled={formDisabled}
                      />
                    </>
                  )}
                </div>
              </div>
            </>
          )}
          <div>
            <div
              className="btn btn-primary m-2 d-print-none"
              onClick={() => {
                handleSubmit(null, null, null, null);
              }}
            >
              Save
            </div>
            <Signature
              canvasWidth="450"
              canvasHeight="200"
              modalTitle="Report Signatures"
              careTeams={careTeams}
              signatures={signatures}
              onSign={saveSignature}
              clientData={clientData.client}
              showModal={showSignatureModal}
              onHide={() => {
                setShowSignatureModal(false);
              }}
              finalSignType={
                formsyConfig.HelperConfiguration &&
                JSON.parse(formsyConfig.HelperConfiguration[0]).finalSignType
              }
              initialSignatureType={
                formsyConfig.HelperConfiguration &&
                JSON.parse(formsyConfig.HelperConfiguration[0]).signatureType
              }
            ></Signature>
            <Comment
              showModal={showComment}
              commentToEdit={commentToEdit}
              onHide={() => {
                setShowComment(false);
                setCommentToEdit(undefined);
              }}
              onSave={onCommentSave}
              fields={commentFields()}
            ></Comment>
            {showShare ? (
              <ShareModal
                showShare={showShare}
                onHide={() => setShowShare(false)}
                onSend={handleShareSend}
                submissionId={submissionid}
              ></ShareModal>
            ) : null}
          </div>
        </div>
      </div>
    </>
  );
}

export default OHSafetyPlan;
